'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormSelect } from '@/components/forms/form-select';
import { FormCombobox } from '@/components/forms/form-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';

import { updateShelfSchema } from '../schemas';
import { useUpdateShelf } from '../services/shelves.mutations';
import {
  storageLocationKeys,
  storageLocationsApi
} from '@/features/storage-locations';
import type { Shelf } from '../types';

interface ShelfEditDialogProps {
  shelf: Shelf | null;
  onOpenChange: (open: boolean) => void;
}

export function ShelfEditDialog({ shelf, onOpenChange }: ShelfEditDialogProps) {
  const t = useTranslations('shelves');
  const tv = useTranslations('shelves.validation');

  const storageLocationsSelect = useInfiniteSelect({
    queryKey: storageLocationKeys.lists(),
    queryFn: (params) => storageLocationsApi.getAll(params),
    mapOption: useCallback(
      (loc: { id: number; name: string }) => ({
        value: String(loc.id),
        label: loc.name
      }),
      []
    )
  });

  const schema = useMemo(() => updateShelfSchema((key) => tv(key)), [tv]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      shelf_type: '',
      slot: '',
      max_capacity: '' as unknown as number,
      status: undefined as 'active' | 'full' | 'maintenance' | undefined,
      storage_location_id: undefined as string | undefined
    }
  });

  useEffect(() => {
    if (shelf) {
      form.reset({
        shelf_type: shelf.shelf_type ?? '',
        slot: shelf.slot ?? '',
        max_capacity: shelf.max_capacity,
        status: shelf.status,
        storage_location_id:
          shelf.storage_location_id != null
            ? String(shelf.storage_location_id)
            : undefined
      });
    }
  }, [shelf, form]);

  const updateMutation = useUpdateShelf();

  const onSubmit = async (data: any) => {
    if (!shelf) return;
    try {
      await updateMutation.mutateAsync({
        id: shelf.id,
        data: {
          shelf_type: data.shelf_type || undefined,
          slot: data.slot || undefined,
          max_capacity: Number(data.max_capacity),
          status: data.status,
          storage_location_id:
            data.storage_location_id != null
              ? Number(data.storage_location_id)
              : undefined
        }
      });
      onOpenChange(false);
    } catch {
      // Error handling via mutation meta
    }
  };

  const statusOptions = [
    { value: 'active', label: t('status.active') },
    { value: 'full', label: t('status.full') },
    { value: 'maintenance', label: t('status.maintenance') }
  ];

  return (
    <Dialog open={!!shelf} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>{t('form.editTitle')}</DialogTitle>
        </DialogHeader>

        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className='space-y-4'
        >
          <FormCombobox
            control={form.control}
            name='storage_location_id'
            label={t('form.storageLocation')}
            placeholder={t('form.storageLocationPlaceholder')}
            infiniteConfig={storageLocationsSelect}
          />

          <FormInput
            control={form.control}
            name='shelf_type'
            label={t('form.shelfType')}
            placeholder={t('form.shelfTypePlaceholder')}
          />

          <FormInput
            control={form.control}
            name='slot'
            label={t('form.shelfIdLabel')}
            placeholder={t('form.shelfIdPlaceholder')}
          />

          <FormInput
            control={form.control}
            name='max_capacity'
            type='number'
            label={t('form.maxCapacity')}
            placeholder='1'
            min={1}
            max={999}
            required
          />

          <FormSelect
            control={form.control}
            name='status'
            label={t('form.statusLabel')}
            options={statusOptions}
            placeholder={t('filter.allStatuses')}
          />

          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              type='button'
              variant='ghost'
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              {t('form.discard')}
            </Button>
            <Button type='submit' disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t('form.saving') : t('form.save')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
